import { useState } from "react";
import { syncQueue } from "@/lib/syncQueue";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { HAZARD_TEMPLATES, PPE_OPTIONS } from "@/lib/constants";
import SignaturePad from "@/components/SignaturePad";
import RescuePlanTemplateMenu from "@/components/RescuePlanTemplateMenu";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";

export default function JsaForm({ project, initial, onSaved }) {
  const [scopeOfWork, setScopeOfWork] = useState(initial?.scope_of_work || "");
  const [items, setItems] = useState(initial?.items || []);
  const [requiredPpe, setRequiredPpe] = useState(initial?.required_ppe || []);
  const [rescuePlan, setRescuePlan] = useState(initial?.rescue_plan || "");
  const [signatures, setSignatures] = useState(initial?.signatures || []);
  const [sigName, setSigName] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePPE = (p) => setRequiredPpe(
    requiredPpe.includes(p) ? requiredPpe.filter((x) => x !== p) : [...requiredPpe, p]
  );

  const addTemplate = (t) => {
    setItems([...items, { activity: t.name, hazards: [...t.hazards], mitigations: [...t.mitigations] }]);
  };

  const addBlank = () => setItems([...items, { activity: "", hazards: [], mitigations: [] }]);

  const updateItem = (i, key, value) => {
    const copy = items.slice();
    copy[i] = { ...copy[i], [key]: value };
    setItems(copy);
  };

  const addStringTo = (i, key, value) => {
    if (!value.trim()) return;
    const copy = items.slice();
    copy[i] = { ...copy[i], [key]: [...(copy[i][key] || []), value.trim()] };
    setItems(copy);
  };
  const removeStringFrom = (i, key, idx) => {
    const copy = items.slice();
    copy[i] = { ...copy[i], [key]: copy[i][key].filter((_, k) => k !== idx) };
    setItems(copy);
  };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const addSignature = (dataUrl) => {
    if (!sigName.trim()) { toast.error("Enter signer name"); return false; }
    setSignatures([...signatures, { name: sigName.trim(), signature_data: dataUrl, signed_at: new Date().toISOString() }]);
    setSigName("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        scope_of_work: scopeOfWork,
        items, required_ppe: requiredPpe, rescue_plan: rescuePlan, signatures, project_id: project.id, created_by: ""
      };
      const r = await api.post(`/projects/${project.id}/jsa`, body, {
        __offlineQueueable: {
          kind: "jsa",
          projectId: project.id,
          body,
        },
      });
      if (r.queued) {
        toast.info("Saved offline — will sync when back online");
      } else {
        toast.success("JSA saved");
        // Release any photos that were waiting on the JSA right away.
        syncQueue({ silent: true, force: true });
      }
      onSaved?.(body, !!r.queued);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-5 sm:p-6 space-y-5" data-testid="jsa-form">
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-300 block mb-2">Project Scope of Work</Label>
          <Textarea
            rows={4}
            value={scopeOfWork}
            onChange={(e) => setScopeOfWork(e.target.value)}
            placeholder="Describe the complete scope of work for the entire project (covered by this JSA)..."
            className="bg-slate-950 border-slate-700"
            data-testid="jsa-scope"
          />
          <div className="text-[11px] text-slate-500 mt-1">One JSA per project — this is the master safety analysis covering the whole job.</div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-300 block mb-2">Hazard Templates</Label>
          <div className="flex flex-wrap gap-2">
            {HAZARD_TEMPLATES.map((t) => (
              <Button key={t.name} type="button" variant="outline" size="sm" onClick={() => addTemplate(t)} className="border-slate-700 bg-slate-950 hover:bg-slate-800" data-testid={`jsa-tpl-${t.name.replace(/\s+/g, '-').toLowerCase()}`}>
                + {t.name}
              </Button>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addBlank} className="border-slate-700 bg-slate-950 hover:bg-slate-800" data-testid="jsa-add-blank">
              <Plus className="w-3.5 h-3.5 mr-1"/> Blank
            </Button>
          </div>
        </div>

        <div className="space-y-3" data-testid="jsa-items">
          {items.length === 0 && <div className="text-sm text-slate-400">Add at least one activity from the templates above.</div>}
          {items.map((it, i) => (
            <div key={`${i}-${it.task || ""}`} className="bg-slate-950 border border-slate-800 rounded-md p-4" data-testid={`jsa-item-${i}`}>
              <div className="flex justify-between gap-3 mb-2">
                <Input value={it.activity} onChange={(e) => updateItem(i, "activity", e.target.value)} placeholder="Work activity" className="bg-slate-900 border-slate-700 font-display font-bold" data-testid={`jsa-activity-${i}`} />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-400"><X className="w-4 h-4"/></Button>
              </div>
              <StringList label="Hazards" items={it.hazards} accent="red" onAdd={(v) => addStringTo(i, "hazards", v)} onRemove={(idx) => removeStringFrom(i, "hazards", idx)} testid={`jsa-hazards-${i}`} />
              <StringList label="Mitigations" items={it.mitigations} accent="emerald" onAdd={(v) => addStringTo(i, "mitigations", v)} onRemove={(idx) => removeStringFrom(i, "mitigations", idx)} testid={`jsa-mits-${i}`} />
            </div>
          ))}
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-300 block mb-2">Required PPE</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PPE_OPTIONS.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm bg-slate-950 border border-slate-700 rounded-md px-3 py-2 cursor-pointer" data-testid={`jsa-ppe-${p.replace(/\s+/g, '-').toLowerCase()}`}>
                <Checkbox checked={requiredPpe.includes(p)} onCheckedChange={() => togglePPE(p)} />
                <span>{p}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <Label className="text-xs uppercase tracking-wide text-slate-300">Rescue Plan</Label>
            <RescuePlanTemplateMenu
              currentValue={rescuePlan}
              onPick={setRescuePlan}
              testid="jsa-rescue-tpl"
            />
          </div>
          <Textarea
            rows={5}
            value={rescuePlan}
            onChange={(e) => setRescuePlan(e.target.value)}
            placeholder="Describe the rescue plan for this project, or tap Use Template to insert the standard plan..."
            className="bg-slate-950 border-slate-700"
            data-testid="jsa-rescue"
          />
        </div>

        <div className="pt-2 border-t border-slate-800">
          <Label className="text-xs uppercase tracking-wide text-slate-300 block mb-2">Crew Signatures ({signatures.length})</Label>
          <div className="flex flex-wrap gap-2 mb-3">
            {signatures.map((s, i) => (
              <div key={`${i}-${s.signer_name || ""}`} className="bg-slate-950 border border-slate-700 rounded-md p-2 flex items-center gap-2" data-testid={`jsa-sig-${i}`}>
                <img src={s.signature_data} alt={`Signature of ${s.name}`} className="h-12 bg-slate-800 border border-slate-700 rounded" />
                <div className="text-xs">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-slate-400">{new Date(s.signed_at).toLocaleString()}</div>
                </div>
                <button onClick={() => setSignatures(signatures.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-400"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-md p-3">
            <Input placeholder="Signer name" value={sigName} onChange={(e) => setSigName(e.target.value)} className="bg-slate-900 border-slate-700 mb-2" data-testid="jsa-sig-name" />
            <SignaturePad onSave={addSignature} testid="jsa-sig-pad" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="jsa-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            SAVE JSA
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StringList({ label, items, onAdd, onRemove, accent = "slate", testid }) {
  const [v, setV] = useState("");
  const accentMap = {
    red: "bg-red-950/40 border-red-900 text-red-300",
    emerald: "bg-emerald-950/40 border-emerald-900 text-emerald-300",
    slate: "bg-slate-800 border-slate-700 text-slate-200",
  };
  return (
    <div className="mt-2">
      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(v); setV(""); } }} placeholder={`Add ${label.toLowerCase()}...`} className="bg-slate-900 border-slate-700 h-8" data-testid={`${testid}-input`} />
        <Button type="button" size="sm" onClick={() => { onAdd(v); setV(""); }} className="bg-blue-700 hover:bg-blue-600 h-8" data-testid={`${testid}-add`}><Plus className="w-3 h-3"/></Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((x, i) => (
          <span key={`${i}-${x}`} className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded border ${accentMap[accent]}`} data-testid={`${testid}-chip-${i}`}>
            {x}<button onClick={() => onRemove(i)}><X className="w-3 h-3"/></button>
          </span>
        ))}
      </div>
    </div>
  );
}
